'use client';

import { useCallback, useRef } from 'react';

/** The focusable controls a re-assert may restore focus to. A pointerdown that
 *  lands inside one of these (the chat textarea, a button) should keep that
 *  control focused even though a trapped FocusScope tries to pull it back. */
const FOCUSABLE_SELECTOR =
  'input, textarea, select, button, [contenteditable], [tabindex]';

/**
 * The pointerdown handler body, extracted as a PURE function so it can be unit
 * tested without a DOM render. It always `stopPropagation`s (preserving the
 * dismiss shield), then — when the event landed on a focusable control — schedules
 * a focus re-assert on the next frame, AFTER the trapped FocusScope's synchronous
 * `focusout` pull-back has run. `preventScroll: true` matches Radix's own `focus()`
 * so re-asserting never jumps the viewport.
 *
 * `raf` and `activeElement` are injected so a test can drive the scheduling and the
 * already-focused short-circuit deterministically.
 */
export function reassertFocus(
  event: Pick<Event, 'stopPropagation' | 'target'>,
  deps: {
    raf: (cb: () => void) => void;
    getActiveElement: () => Element | null;
  },
): void {
  event.stopPropagation();
  const target = event.target as Element | null;
  const focusable = target?.closest<HTMLElement>(FOCUSABLE_SELECTOR) ?? null;
  if (!focusable) return;
  deps.raf(() => {
    if (deps.getActiveElement() !== focusable) {
      focusable.focus({ preventScroll: true });
    }
  });
}

export function shouldShieldFocusOut(
  event: Pick<FocusEvent, 'relatedTarget'>,
  isInsideShield: (target: EventTarget | null) => boolean,
): boolean {
  return isInsideShield(event.relatedTarget);
}

export function shouldShieldFocusIn(
  event: Pick<FocusEvent, 'target'>,
  isInsideShield: (target: EventTarget | null) => boolean,
): boolean {
  return isInsideShield(event.target);
}

export function shouldPreventDismissableLayerOutside(
  event: Event,
  isInsideShield: (target: EventTarget | null) => boolean,
): boolean {
  const originalEvent =
    event instanceof CustomEvent
      ? (event.detail as { originalEvent?: Event } | undefined)?.originalEvent
      : undefined;

  return isInsideShield(originalEvent?.target ?? null);
}

/**
 * A callback ref that shields its element from being treated as an "outside"
 * interaction by any Radix `DismissableLayer`, vaul drawer, or Radix `FocusScope`.
 * All of those detect outside clicks/focus via document-level `pointerdown` /
 * `focusin` / `focusout` listeners (bubble phase) — so without this, clicking the
 * Setup Assistant while a drawer/dialog/sheet is open is read as an outside click
 * and dismisses it (and, for modal layers, the focus scope yanks the caret back
 * out of the chat input).
 *
 * We attach NATIVE bubble listeners on the element that `stopPropagation`, so the
 * event is stopped one level below `document` and never reaches those listeners.
 * The open form the user is filling stays put, and the chat input keeps focus.
 *
 * A trapped Radix `FocusScope` is the hard case: it pulls focus back via a
 * document-level `focusout` that fires from a node INSIDE the dialog when focus
 * moves to the chat textarea. A panel-level listener can't stop that because the
 * event originates outside this element, so we also install a temporary
 * document-capture focusout guard while the panel is mounted. It only stops
 * focusout events whose destination is inside this shield, so the active form's
 * focus trap still protects the rest of the page.
 *
 * Why NATIVE (not a React `onPointerDown`): Next.js App Router hydrates on
 * `document`, so React's delegated listeners AND Radix's listeners share the SAME
 * `document` node — and React's `stopPropagation` cannot stop a same-node native
 * listener. A real listener on THIS element runs below `document`, before any of
 * them see the event. A callback ref (re)binds correctly when the panel mounts and
 * unmounts with `isOpen`.
 */
export function useDismissShield<T extends HTMLElement>() {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((node: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node) return;

    // A pointerdown on a drag handle must bubble to React's root so the panel's
    // onPointerDown drag handler fires — so neither the node-level reassert (which
    // stopPropagation's) nor the mousedown stop swallow it.
    const isDragHandle = (event: Event) =>
      event.target instanceof Element &&
      event.target.closest('[data-setup-drag-handle]') != null;
    const stop = (event: Event) => {
      if (isDragHandle(event)) return;
      event.stopPropagation();
    };
    const reassert = (event: Event) => {
      if (isDragHandle(event)) return;
      reassertFocus(event, {
        raf: (cb) => requestAnimationFrame(cb),
        getActiveElement: () => document.activeElement,
      });
    };
    const stopFocusOutIntoShield = (event: FocusEvent) => {
      if (
        shouldShieldFocusOut(
          event,
          (target) => target instanceof Node && node.contains(target),
        ) ||
        node.contains(document.activeElement)
      ) {
        event.stopImmediatePropagation();
      }
    };
    const stopFocusInInsideShield = (event: FocusEvent) => {
      if (
        shouldShieldFocusIn(
          event,
          (target) => target instanceof Node && node.contains(target),
        )
      ) {
        event.stopImmediatePropagation();
      }
    };
    const stopPointerInsideShield = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node) || !node.contains(target)) return;

      // A drag handle must RECEIVE the pointerdown so the panel can be dragged —
      // so we don't swallow it here. The `dismissableLayer.pointerDownOutside`
      // guard below still prevents an open drawer/dialog from closing, so letting
      // the event through is safe.
      if (
        target instanceof Element &&
        target.closest('[data-setup-drag-handle]')
      ) {
        return;
      }

      event.stopImmediatePropagation();

      const focusable =
        target instanceof Element
          ? target.closest<HTMLElement>(FOCUSABLE_SELECTOR)
          : null;
      if (focusable) {
        requestAnimationFrame(() => {
          if (document.activeElement !== focusable) {
            focusable.focus({ preventScroll: true });
          }
        });
      }
    };
    const preventDismissableLayerOutside = (event: Event) => {
      if (
        shouldPreventDismissableLayerOutside(
          event,
          (target) => target instanceof Node && node.contains(target),
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    node.addEventListener('pointerdown', reassert);
    node.addEventListener('mousedown', stop);
    node.addEventListener('focusin', stop);
    node.addEventListener('focusout', stop);
    window.addEventListener('pointerdown', stopPointerInsideShield, true);
    window.addEventListener('mousedown', stopPointerInsideShield, true);
    window.addEventListener('focusin', stopFocusInInsideShield, true);
    window.addEventListener('focusout', stopFocusOutIntoShield, true);
    document.addEventListener('focusin', stopFocusInInsideShield, true);
    document.addEventListener('focusout', stopFocusOutIntoShield, true);
    document.addEventListener(
      'dismissableLayer.pointerDownOutside',
      preventDismissableLayerOutside,
      true,
    );
    document.addEventListener(
      'dismissableLayer.focusOutside',
      preventDismissableLayerOutside,
      true,
    );

    cleanupRef.current = () => {
      node.removeEventListener('pointerdown', reassert);
      node.removeEventListener('mousedown', stop);
      node.removeEventListener('focusin', stop);
      node.removeEventListener('focusout', stop);
      window.removeEventListener('pointerdown', stopPointerInsideShield, true);
      window.removeEventListener('mousedown', stopPointerInsideShield, true);
      window.removeEventListener('focusin', stopFocusInInsideShield, true);
      window.removeEventListener('focusout', stopFocusOutIntoShield, true);
      document.removeEventListener('focusin', stopFocusInInsideShield, true);
      document.removeEventListener('focusout', stopFocusOutIntoShield, true);
      document.removeEventListener(
        'dismissableLayer.pointerDownOutside',
        preventDismissableLayerOutside,
        true,
      );
      document.removeEventListener(
        'dismissableLayer.focusOutside',
        preventDismissableLayerOutside,
        true,
      );
    };
  }, []);
}
